// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: an automation's repeat rule in words ("Last Monday of every month", "Every 2 weeks on
// Monday, Thursday"), for the Scheduled page chips and the automation review card. No stores
// here, so the card can use it without loading the Scheduled page.

import type {FlowRepeat} from '@konvo/schema/src/domains/konvo/AssistantFlow';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

export const WEEKDAYS_PLURAL: Record<number, MessageDescriptor> = {
	1: msg({message: 'Mondays', comment: 'Scheduled page repeat chip: every Monday.'}),
	2: msg({message: 'Tuesdays', comment: 'Scheduled page repeat chip: every Tuesday.'}),
	3: msg({message: 'Wednesdays', comment: 'Scheduled page repeat chip: every Wednesday.'}),
	4: msg({message: 'Thursdays', comment: 'Scheduled page repeat chip: every Thursday.'}),
	5: msg({message: 'Fridays', comment: 'Scheduled page repeat chip: every Friday.'}),
	6: msg({message: 'Saturdays', comment: 'Scheduled page repeat chip: every Saturday.'}),
	7: msg({message: 'Sundays', comment: 'Scheduled page repeat chip: every Sunday.'}),
};

const RULE_LABEL = {
	days: msg({
		message: '{count, plural, one {Every day} other {Every # days}}',
		comment: 'Repeat of an automation: every day or every few days.',
	}),
	weeks: msg({
		message: '{count, plural, one {Every week on {days}} other {Every # weeks on {days}}}',
		comment: 'Repeat of an automation, e.g. "Every 2 weeks on Monday, Thursday". {days} lists weekday names.',
	}),
	months: msg({
		message: '{count, plural, one {{day} of every month} other {{day} of every # months}}',
		comment:
			'Repeat of an automation, e.g. "Last Monday of every month" or "15th of every 3 months". {day} is a day phrase like "Last Monday".',
	}),
	years: msg({
		message: '{count, plural, one {{day} of {month}, every year} other {{day} of {month}, every # years}}',
		comment: 'Repeat of an automation, e.g. "Last Friday of November, every year". {month} is a month name.',
	}),
	nthWeekday: msg({
		message: '{week, select, 1 {First} 2 {Second} 3 {Third} 4 {Fourth} 5 {Fifth} other {Last}} {weekday}',
		comment:
			'Day phrase in a monthly repeat, e.g. "Last Monday" or "Second Tuesday". {weekday} is a weekday name; week is 1-5, anything else means the last one.',
	}),
	dayOfMonth: msg({
		message: '{day, selectordinal, one {#st} two {#nd} few {#rd} other {#th}}',
		comment: 'Day phrase in a monthly repeat: a day of the month as an ordinal, e.g. "15th".',
	}),
	lastDay: msg({message: 'Last day', comment: 'Day phrase in a monthly repeat: the last day of the month.'}),
};

function weekdayName(i18n: I18n, weekday: number): string {
	// 2024-01-01 was a Monday.
	return new Date(2024, 0, weekday).toLocaleDateString(i18n.locale, {weekday: 'long'});
}

/** "Last Monday of every month", "Every 2 weeks on Monday, Thursday", "15th of October, every year". */
export function ruleText(i18n: I18n, rule: FlowRepeat, at: string): string {
	const anchor = new Date(at);
	const valid = !Number.isNaN(anchor.getTime());
	const count = rule.interval ?? 1;
	const anchorWeekday = valid ? ((anchor.getDay() + 6) % 7) + 1 : 1;
	const day = () => {
		if (rule.nth_weekday)
			return i18n._(RULE_LABEL.nthWeekday, {
				week: String(rule.nth_weekday.week),
				weekday: weekdayName(i18n, rule.nth_weekday.weekday),
			});
		if (rule.day_of_month === -1) return i18n._(RULE_LABEL.lastDay);
		return i18n._(RULE_LABEL.dayOfMonth, {day: rule.day_of_month ?? (valid ? anchor.getDate() : 1)});
	};
	switch (rule.every) {
		case 'day':
			return i18n._(RULE_LABEL.days, {count});
		case 'week': {
			const weekdays = [...new Set(rule.weekdays ?? [anchorWeekday])].sort((a, b) => a - b);
			if (count === 1) return weekdays.map((weekday) => i18n._(WEEKDAYS_PLURAL[weekday])).join(', ');
			return i18n._(RULE_LABEL.weeks, {count, days: weekdays.map((weekday) => weekdayName(i18n, weekday)).join(', ')});
		}
		case 'month':
			return i18n._(RULE_LABEL.months, {count, day: day()});
		case 'year':
			return i18n._(RULE_LABEL.years, {
				count,
				day: day(),
				month: valid ? anchor.toLocaleDateString(i18n.locale, {month: 'long'}) : '',
			});
	}
}
